import { Component, ElementRef, OnInit } from '@angular/core';
import {
  NavbarComponent,
  SelectedCustomerService,
  AuthService,
  CartService,
  AssortmentService,
  SimpleRoute,
  NavbarConfigService,
  DocumentsService,
  Document,
  GungFlowService,
  PriceConfigService,
  NavGroup,
  NavLink,
  GlobalSearchConfigService,
  Customer
} from 'gung-standard';
import { takeUntil, switchMap, tap, filter, first } from 'rxjs';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

interface ColmecSimpleRoute extends SimpleRoute {
  queryParams?: {
    [key: string]: string;
  };
  queryParamsHandling?: string;
}

@Component({
    selector: 'colmec-navbar',
    templateUrl: './colmec-navbar.component.html',
    styleUrls: ['./colmec-navbar.component.scss'],
    standalone: false
})
export class ColmecNavbarComponent extends NavbarComponent implements OnInit {
  subassortments: SimpleRoute[] = [];
  extraLinks: SimpleRoute[];

  documentsList: { url: string; document: Document }[];
  isDocumentListFetched = false;
  onlyUserPermission = true;
  isAdmin = false;
  isNoPrice = false;
  constructor(
    protected selectedCustomerService: SelectedCustomerService,
    protected authService: AuthService,
    protected cartService: CartService,
    protected elementRef: ElementRef,
    protected navbarConfigService: NavbarConfigService,
    protected gungFlowService: GungFlowService,
    protected router: Router,
    private assortmentService: AssortmentService,
    private documentsService: DocumentsService,
    protected priceConfigService: PriceConfigService,
    protected globalSearchConfigService: GlobalSearchConfigService
  ) {
    super(selectedCustomerService, authService, cartService, elementRef, navbarConfigService, gungFlowService, router, globalSearchConfigService);

    this.authService
      .getIsAuthenticated()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(auth => {
        if (auth) {
          this.assortmentService
            .getUserAssortment()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(assortment => {
              const subAssortments = assortment.children
                .filter(a => a.id && a.id.length > 0 && a.id[0] === 's')
                .map(a => {
                  return { route: '/categories/' + a.id, heading: a.name };
                });

              if (subAssortments && subAssortments.length !== 0) {
                subAssortments.forEach(subAssortment => this.subassortments.push(subAssortment));
              } else {
                subAssortments.push({ route: '/products', heading: 'SHOP_BY_PRODUCT_LIST' });
              }

              // In the future implementation
              // this.extraLinks = this.extraLinks.concat(subassortments);
            });
        }
      });
  }

  ngOnInit() {
    this.change
      .asObservable()
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap(() => {
          return this.authService.getIsAuthenticated().pipe(
            tap(authed => (this.signedIn = authed)),
            filter(authenticated => !!authenticated),
            tap(authenticated => (this.authenticated = authenticated)),
            switchMap(() =>
              forkJoin([
                this.authService.getRoles().pipe(
                  filter(roles => !!roles),
                  first()
                ),
                this.selectedCustomerService.getSelectedCustomer().pipe(
                  filter(customer => !!customer),
                  first()
                )
              ])
            )
          );
        })
      )
      .subscribe(([roles, customer]) => {
        this.selectedCustomer = customer;
        if (roles.indexOf('NOPRICE') > -1) {
          this.isNoPrice = true;
        }
        if (roles.indexOf('SALES') > -1 || roles.indexOf('ADMIN') > -1) {
          this.onlyUserPermission = false;
          if (roles.indexOf('ADMIN') > -1) {
            this.isAdmin = true;
          }
          this.isSales = true;
          this.extraLinks = this.getSalesMenu();
          this.navbarConfigService.getMainMenu().subscribe(menu => {
            this.extraLinks = this.extraLinks || [];
            const documents = menu.links.find(m => m.heading === 'DOCUMENTS') as NavGroup;
            const system = menu.links.find(m => m.heading === 'SYSTEM') as NavGroup;
            const digitalAssets = { route: '/digital-assets', heading: 'DIGITAL_ASSETS' } as SimpleRoute;
            this.extraLinks.push(digitalAssets);
            if (this.isAdmin) {
              const discounts = { route: '/discounts', heading: 'DISCOUNTS' } as SimpleRoute;
              this.extraLinks.push(discounts);
            }

            if (documents) {
              this.extraLinks.push(...documents.links.map((d: NavLink) => ({ heading: d.heading, route: d.link, queryParams: d.queryParams })));
            }
            if (system) {
              this.extraLinks.push(...system.links.map((d: NavLink) => ({ heading: d.heading, route: d.link, queryParams: d.queryParams })));
            }

          });
        } else {
          this.isSales = false;
          this.extraLinks = this.getCustomerMenu(customer);
        }
        // this.extraLinks.push({ route: '/claims', heading: 'CLAIMS' });

        this.getDocuments();
      });

    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => this.change.next(true));

    this.cartService
      .getCurrentCart()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(cart => (this.cart = cart));

    this.isCollapsed = true;
    this.isCartVisible = false;
    this.isCustomerCollapsed = true;
  }

  getDocuments() {
    if (!this.isDocumentListFetched) {
      this.isDocumentListFetched = true;
      this.documentsService
        .getDocuments()
        .pipe(first())
        .subscribe(documents => {
          this.documentsList = [];
          documents.forEach(document => {
            if (!!document.metaData && !!document.metaData?.inMenu) {
              this.documentsList.push({
                url: this.getDocumentUrl(document.id),
                document
              });
            }
          });
        });
    }
  }

  protected getSalesMenu(): ColmecSimpleRoute[] {
    const baseMenu: ColmecSimpleRoute[] = [
      {
        route: '/open-orders',
        heading: 'OPEN_ORDERS'
      }
    ];

    if (!this.isNoPrice) {
      baseMenu.push({
        route: '/invoices',
        heading: 'INVOICES'
      });
    }

    if (environment.enableClaim) {
      baseMenu.push({
        route: '/claims',
        heading: 'CLAIMS'
        // icon: 'fa-user'
      });
    }

    baseMenu.push({
      route: '/activity-internal',
      heading: 'ACTIVITIES'
    });

    baseMenu.push({
      route: '/partner-agreements',
      queryParams: {
        filters: 'STATUS__:__ACTIVE'
      },
      queryParamsHandling: '',
      heading: 'PARTNER_AGREEMENTS'
    });

    baseMenu.push({
      route: '/work-orders',
      heading: 'WORK_ORDERS'
    });

    return baseMenu;
  }

  protected getCustomerMenu(customer: Customer): SimpleRoute[] {
    let links = [
      {
        route: `${'/customers/' + customer.id}`,
        heading: 'MY_PAGES'
      },
      {
        route: '/orders',
        heading: 'PREVIOUS_ORDERS'
      },
      {
        route: '/digital-assets',
        heading: 'DIGITAL_ASSETS'
      }
    ];

    if (customer.extra.kus?.q_partner_boolean) {
      links.push({
        route: '/work-orders',
        heading: 'WORK_ORDERS'
      });
    }

    return links;
  }

  public toggleMobileMenu() {
    this.baseCollapseClasses.show = !this.baseCollapseClasses.show;
    this.isCollapsed = this.baseCollapseClasses.show;
  }

  getDocumentUrl(id): string {
    return this.documentsService.getDocumentUrl(id);
  }

  hidePrice(inputElement: HTMLInputElement) {
    this.priceConfigService.setHidePrice(inputElement.checked);
  }
}
